import React from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import { Button } from '@/components/Button';
import { Alert } from '@/components/Icons/Alert';
import { SubscriptionStar } from '@/components/Icons/SubscriptionStar';
import { ROUTE_SUBSCRIPTION } from '@/router/routes';
import { ButtonVariant, Size } from '@/types/components';
import { SubscriptionState, type SubscriptionBannerState } from '@/types/user';
import { tv } from '@/utils/tv';

const message = tv({
  base: 'text-body-16',
  variants: {
    state: {
      [SubscriptionState.Inactive]: 'text-subtle-foreground',
      [SubscriptionState.ExpiringSoon]: 'font-semibold text-destructive',
    },
  },
});

const stateIcons = {
  [SubscriptionState.Inactive]: (
    <SubscriptionStar className="hidden size-6 shrink-0 text-primary md:block" />
  ),
  [SubscriptionState.ExpiringSoon]: (
    <Alert className="hidden size-6 shrink-0 text-warning md:block" />
  ),
};

interface Props {
  subscription: SubscriptionBannerState;
}

export const ExpirationBanner: React.FC<Props> = ({ subscription }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const messageText =
    subscription.kind === SubscriptionState.ExpiringSoon
      ? t('home.banner.daysRemaining', { count: subscription.daysRemaining })
      : t('home.banner.subtitle');

  const ctaLabel =
    subscription.kind === SubscriptionState.Inactive
      ? t('home.banner.ctaBuy')
      : t('home.banner.ctaProlong');

  return (
    <div className="px-4 pt-3 md:px-6 md:pt-6">
      <div className="flex items-center justify-between gap-3 rounded-xl bg-card p-4 shadow-card md:px-6">
        <div className="flex min-w-0 flex-1 items-center gap-2">
          {stateIcons[subscription.kind]}
          <div className="flex min-w-0 flex-col gap-1 md:flex-row md:items-center">
            <p className="text-[18px]/[24px] font-semibold text-foreground md:text-callout-16 md:font-medium md:after:content-[':']">
              {t('home.banner.title')}
            </p>
            <p className={message({ state: subscription.kind })}>
              {messageText}
            </p>
          </div>
        </div>
        <Button
          variant={ButtonVariant.Primary}
          size={Size.Small}
          className="py-[7px] text-[13px]/[18px] font-semibold"
          onClick={() => navigate(ROUTE_SUBSCRIPTION)}
        >
          {ctaLabel}
        </Button>
      </div>
    </div>
  );
};
